// ------------------------------------------
// 私たちの4つの強み
// ------------------------------------------
class Strengths {
  constructor(el, opt) {
    this.items = Array.from(document.querySelectorAll('.' + el));
    if (this.items.length > 0) {
      this.duration = 0.3;
      this.limit = 6;
      this.alignMax = 12;
      this.cards = [];
      this.visible = false;
      this.event();
    }
  }

  event() {
    this.toggle();
    this.stack();
    this.inertia();
  }

  // ------------------------------
  // カードの重なり
  // ------------------------------
  stack() {
    this.update = this.update.bind(this);

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(this.update);
      this.items.forEach((item) => observer.observe(item.querySelector('.p-top__strengths-inner') || item));
    }

    let timer;
    window.addEventListener('resize', () => {
      clearTimeout(timer);
      timer = setTimeout(this.update, 200);
    });

    window.addEventListener('load', this.update);

    this.update();
  }

  clearSpacing(list, heading) {
    list.style.removeProperty('--strengths-top');
    list.style.removeProperty('--strengths-space');
    list.style.marginTop = '';
    if (heading) heading.style.marginBottom = '';
    this.items.forEach((item) => {
      item.style.marginBottom = '';
      item.style.transform = '';
    });
  }

  update() {
    const list = this.items[0].parentElement;
    const section = list.parentElement;
    const heading = section.querySelector('.p-top__strengths-heading');

    if (window.matchMedia('(max-width: 767px)').matches) {
      section.style.removeProperty('--strengths-pin');
      section.style.removeProperty('--strengths-rest');
      this.clearSpacing(list, heading);
      this.cards = [];
      return;
    }

    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const gap = parseFloat(getComputedStyle(list).marginTop) + parseFloat(getComputedStyle(heading).marginBottom);

    // 余白をリセットしてから実寸を測る
    this.clearSpacing(list, heading);
    list.style.setProperty('--strengths-top', heading.offsetHeight + gap + 'px');

    const measure = () => this.items.map((item) => {
      const style = getComputedStyle(item);
      return {
        item,
        top: parseFloat(style.top) || 0,
        height: item.offsetHeight,
      };
    });
    const bottomOf = (cards) => Math.max(...cards.map((card) => card.top + card.height));

    section.style.setProperty('--strengths-pin', '0px');
    const space = Math.round(window.innerHeight - bottomOf(measure()));
    const alignMax = this.alignMax * rem;
    const pin = Math.round(space <= alignMax ? space : Math.max(alignMax, space / 2));
    section.style.setProperty('--strengths-pin', pin + 'px');

    const cards = measure();
    const stackBottom = bottomOf(cards);

    section.style.setProperty('--strengths-rest', Math.max(0, Math.round(window.innerHeight - stackBottom)) + 'px');

    const release = Math.max(0, Math.round(stackBottom - pin - heading.offsetHeight));
    heading.style.marginBottom = release + 'px';
    list.style.marginTop = gap - release + 'px';

    // 固定が外れるタイミングを全カードで揃える（各カードの下端 + margin-bottom = stackBottom）
    cards.forEach((card, i) => {
      const isLast = i === cards.length - 1;
      card.space = isLast ? 0 : Math.max(0, Math.round(stackBottom - card.top - card.height));
      card.item.style.marginBottom = card.space + 'px';
      card.box = card.height + card.space;
    });

    const y = window.scrollY;
    let flow = list.getBoundingClientRect().top + y + (parseFloat(getComputedStyle(list).paddingTop) || 0);
    cards.forEach((card) => {
      card.flow = flow;
      flow += card.box;
      card.pos = Math.max(card.flow - y, card.top);
      card.item.style.transform = '';
    });

    this.listTop = cards[0].flow;
    this.limitPx = this.limit * rem;
    this.cards = cards;
  }

  // ------------------------------
  // 収まるときの余韻
  // ------------------------------
  inertia() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || typeof gsap === 'undefined') return;

    this.tick = this.tick.bind(this);

    const observer = new IntersectionObserver((entries) => {
      this.visible = entries[0].isIntersecting;
      this.visible ? this.start() : this.reset();
    }, { rootMargin: '20% 0px' });
    observer.observe(this.items[0].parentElement);

    window.addEventListener('scroll', () => this.start(), { passive: true });
  }

  start() {
    if (this.running || !this.visible) return;
    this.running = true;
    this.last = 0;
    gsap.ticker.add(this.tick);
  }

  reset() {
    this.cards.forEach((card) => {
      card.pos = Math.max(card.flow - window.scrollY, card.top);
      card.item.style.transform = '';
    });
  }

  // セクションを過ぎたあとは sticky / 慣性を即クリアする
  isPast() {
    if (!this.cards.length) return false;
    const last = this.cards[this.cards.length - 1];
    return window.scrollY > last.flow + last.height;
  }

  tick(seconds) {
    const time = seconds * 1000;
    const dt = Math.min(time - (this.last || time), 100) / 1000;
    this.last = time;

    if (!this.visible || this.isPast()) {
      gsap.ticker.remove(this.tick);
      this.reset();
      this.running = false;
      return;
    }

    const y = window.scrollY;
    let moving = false;

    this.cards.forEach((card, i) => {
      const target = Math.max(card.flow - y, card.top);

      if (i === 0) {
        card.pos = target;
        card.item.style.transform = '';
        return;
      }

      // すでに通過したカードは慣性をかけない（通過後に単体でずれるのを防ぐ）
      if (y > card.flow + card.height) {
        card.pos = target;
        card.item.style.transform = '';
        return;
      }

      const distance = target - card.pos;

      if (Math.abs(distance) > window.innerHeight) {
        card.pos = target;
      } else {
        const speed = this.duration / (1 + Math.abs(distance) / this.limitPx);
        card.pos += distance * (1 - Math.exp(-dt / speed));
      }

      let offset = card.pos - target;
      if (offset < 0) offset = Math.max(offset, -Math.max(0, target - (this.listTop - y)));

      if (Math.abs(offset) < 0.3) {
        card.pos = target;
        card.item.style.transform = '';
      } else {
        moving = true;
        card.item.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
      }
    });

    if (!moving) {
      gsap.ticker.remove(this.tick);
      this.reset();
      this.running = false;
    }
  }

  // ------------------------------
  // SPの開閉
  // ------------------------------
  toggle() {
    this.items.forEach((item) => {
      const btn = item.querySelector('.js-strength-toggle');
      if (!btn) return;

      btn.addEventListener('click', () => {
        const isOpen = item.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    });
  }
}
